import React, { useState } from "react";
import { ShieldCheck, BookOpen, Award, Phone, Video, Smartphone, Send } from "lucide-react";
import { sendOtp, verifyOtp } from "../../api/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { storeAuthData } from "../../api/storageService";
import { useNavigate } from "react-router-dom";

const Register = () => {
    const [phone, setPhone] = useState("");
    const [firstName, setFirstName] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [error, setError] = useState("");
    const [agreeTerms, setAgreeTerms] = useState(true);
    const navigate = useNavigate();

    const handleSendOtp = async () => {
        // Validate first name - shows toast when submitting
        if (!firstName.trim()) {
            toast.error("Please enter your first name");
            return;
        }

        if (!phone || phone.length !== 10) {
            toast.error("Enter valid 10 digit phone number");
            return;
        }

        if (!agreeTerms) {
            toast.error("Please agree to Terms of Service and Privacy Policy");
            return;
        }

        try {
            setLoading(true);
            const res = await sendOtp(phone, "weblogin");

            if (res.status) {
                setOtpSent(true);
                toast.success("OTP sent successfully ✅");
            } else {
                toast.error(res.message || "Failed to send OTP");
            }
        } catch (err) {
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    // In Register.jsx, update handleVerifyOtp function
    const handleVerifyOtp = async () => {
        if (!firstName.trim()) {
            toast.error("Please enter your first name");
            return;
        }

        if (!otp || otp.length !== 4) {
            toast.error("Enter valid 4-digit OTP");
            return;
        }

        if (!agreeTerms) {
            toast.error("Please agree to Terms of Service and Privacy Policy");
            return;
        }

        try {
            setLoading(true);
            const res = await verifyOtp(phone, otp, "weblogin");

            if (res.status) {
                storeAuthData(res); // Store auth data
                toast.success("Registration successful 🎉");
                navigate("/dashboard"); // Redirect to dashboard
            } else {
                toast.error("Invalid OTP");
            }
        } catch (err) {
            toast.error("OTP verification failed");
        } finally {
            setLoading(false);
        }
    };

    const handleTermsClick = () => {
        navigate("/terms-conditions");
    };

    const handlePrivacyClick = () => {
        navigate("/privacy-policy");
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="custom-science custom-padding pt-80 pb-80 xl-screen-padding">
                <div className="w-full grid md:grid-cols-10 gap-12">
                    {/* Left Section */}
                    <div className="hidden md:block md:col-span-6">
                        <h1 className="text-lg sm:text-xl custom-line-height md:text-xl lg:text-4xl
 font-bold text-gray-900 leading-tight mb-4 font-lato font-bold">
                            Join Nutrillion
                        </h1>
                        <p className="text-gray-500 mb-4 font-lato text-p">
                            Start your personalized nutrition, wellness, and learning journey with one secure account.                        </p>
                        <p className="text-gray-500 mb-4 font-lato text-p">
                            Create your account to access expert consultations, personalized health programs, evidence-based courses, wellness tracking, certificates, and exclusive learning resources—all in one platform.
                        </p>
                        <p className="text-gray-500 font-bold  mb-4 font-lato text-p">
                            Everything You Need to Achieve Your Health Goals </p>
                        <div className="grid grid-cols-2 gap-4 mb-8 font-lato">
                            {[
                                { icon: <BookOpen size={18} />, title: "Personalized Health Programs", desc: "Receive customized nutrition and wellness plans designed around your unique goals." },
                                { icon: <Award size={18} />, title: "Expert Consultations", desc: "Book appointments and connect with qualified nutrition and wellness professionals." },
                                { icon: <Video size={18} />, title: "Learning & Certifications", desc: "Access evidence-based courses, complete assessments, and earn certificates at your own pace." },
                                { icon: <Smartphone size={18} />, title: "Progress Tracking", desc: "Monitor your health journey, course progress, achievements, and wellness milestones from one dashboard.." },
                            ].map((item, i) => (
                                <div key={i} className="bg-[#f8f9fa] border rounded-xl p-4 font-lato">
                                    <div className="text-green-600 mb-2 font-lato">{item.icon}</div>
                                    <h4 className="text-md font-semibold text-gray-800 font-lato">{item.title}</h4>
                                    <p className="text-sm text-gray-500 font-lato">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Section */}
                    <div className="flex items-center justify-center font-lato md:col-span-4">
                        <div className="w-full max-w-md bg-white border rounded-2xl p-8 shadow-sm">
                            <h2 className="text-md sm:text-lg custom-line-height md:text-lg lg:text-3xl font-semibold text-gray-900 mb-2 font-bold text-center md:text-left">
                                Create your account
                            </h2>

                            <p className="text-p-center text-gray-500 mb-4">
                                Join Nutrition Academy today and unlock your personalized learning dashboard.
                            </p>

                            <div className="text-center text-p-center text-gray-400 mb-4 relative">
                                <span className="bg-white px-2 relative z-10">
                                    Sign up with phone
                                </span>
                                <div className="absolute top-1/2 left-0 w-full h-px bg-gray-200"></div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-4 text-p-center">
                                <div>
                                    <p className="font-semibold text-gray-600 mb-2">
                                        First Name <span className="text-red-500">*</span>
                                    </p>
                                    <input
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        placeholder="Enter first name"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-green-500"
                                    />
                                </div>

                                <div>
                                    <p className="font-semibold text-gray-600 mb-2">
                                        Last Name
                                    </p>
                                    <input
                                        placeholder="Enter last name"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-green-500"
                                    />
                                </div>
                            </div>

                            <p className="text-p-center font-semibold text-gray-600 mb-2">
                                Phone number
                            </p>

                            <div className="relative mb-4">
                                <input
                                    value={phone}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                        setPhone(val);
                                    }}
                                    placeholder="+91 98765 43210"
                                    className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 outline-none text-sm focus:border-green-500"
                                />
                                <Phone
                                    size={18}
                                    className="absolute right-4 top-3.5 text-green-600"
                                />
                            </div>

                            <button
                                onClick={handleSendOtp}
                                className="w-full py-3 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold mb-4 flex items-center justify-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    "Sending..."
                                ) : (
                                    <>
                                        <Send size={16} className="text-gray-600" />
                                        Send OTP
                                    </>
                                )}
                            </button>
                            {otpSent && (
                                <>
                                    <div className="flex justify-between mb-2">
                                        <p className="text-p-center font-semibold text-gray-600">
                                            Enter OTP
                                        </p>
                                        <span
                                            onClick={handleSendOtp}
                                            className="text-p-center text-green-600 font-semibold cursor-pointer hover:underline"
                                        >
                                            Resend code
                                        </span>
                                    </div>

                                    <div className="relative mb-4">
                                        <input
                                            value={otp}
                                            onChange={(e) => {
                                                setOtp(e.target.value.replace(/\D/g, "").slice(0, 4));
                                            }}
                                            placeholder="••••"
                                            className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 outline-none text-sm tracking-widest focus:border-green-500"
                                        />
                                        <ShieldCheck
                                            size={18}
                                            className="absolute right-4 top-3.5 text-green-600"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Terms and Conditions */}
                            <div className="flex items-start gap-2 text-sm text-gray-500 mb-5 w-full">
                                <label className="flex items-start gap-2 cursor-pointer flex-shrink-0 mt-0.5">
                                    <input
                                        type="checkbox"
                                        checked={agreeTerms}
                                        onChange={(e) => setAgreeTerms(e.target.checked)}
                                        className="hidden"
                                    />

                                    <span
                                        className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${agreeTerms
                                            ? "bg-green-600 text-white"
                                            : "border border-gray-300"
                                            }`}
                                    >
                                        {agreeTerms && "✓"}
                                    </span>
                                </label>

                                <div className="flex-1 leading-6">
                                    I have read and agree to the{" "}

                                    <button
                                        onClick={handleTermsClick}
                                        className="text-green-600 font-semibold hover:underline"
                                        type="button"
                                    >
                                        Terms of Service
                                    </button>

                                    {" "}and{" "}

                                    <button
                                        onClick={handlePrivacyClick}
                                        className="text-green-600 font-semibold hover:underline"
                                        type="button"
                                    >
                                        Privacy Policy
                                    </button>

                                    {", and consent to receive service-related communications from Nutrillion."}
                                </div>
                            </div>

                            <div className="flex justify-between text-xs text-gray-400 mb-5">
                                <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full">
                                    🛡 Secure sign up
                                </span>
                            </div>

                            <button
                                onClick={handleVerifyOtp}
                                className="w-full bg-[#15803d] text-white py-3 rounded-xl font-bold hover:bg-[#166534] transition-colors disabled:opacity-50"
                                disabled={loading || !agreeTerms || !firstName.trim()}
                            >
                                {loading ? "Verifying..." : "Create Account →"}
                            </button>

                            <p className="text-center text-sm text-gray-500 mt-5">
                                Already have an account?
                                <button
                                    onClick={() => navigate("/login")}
                                    className="text-green-600 font-semibold ml-1 hover:underline"
                                >
                                    Log In
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Register;